import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { User, LogOut, Settings, Menu, ChevronDown, HelpCircle, Star, FileText, Camera, LayoutDashboard, BookOpen } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { currentCalendarYear } from '@/lib/calendarYear';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  user: { id: string; email?: string | null } | null;
  onSignOut: () => void;
  onShowAuthModal: () => void;
  isRecoveryMode?: boolean;
}

const getNavLinks = (isLoggedIn: boolean) => {
  const links = [
    { to: '/workshops', label: 'Workshops', icon: Camera },
    { to: '/blog', label: 'Blog', icon: FileText },
    { to: '/resources', label: 'Resources', icon: BookOpen },
  ];
  if (!isLoggedIn) {
    links.push({ to: '/features', label: 'Premium', icon: Star });
  }
  return links;
};

export const Header: React.FC<HeaderProps> = ({ user: propUser, onSignOut, onShowAuthModal, isRecoveryMode = false }) => {
  const user = isRecoveryMode ? null : propUser;
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWorkshopLeader, setIsWorkshopLeader] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const plannerTo = user ? '/dashboard' : '/planner';
  const plannerLabel = 'Planner';

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/blog') return location.pathname.startsWith('/blog');
    if (path === '/workshops') return location.pathname.startsWith('/workshops');
    if (path === '/') return false;
    return location.pathname === path;
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
        if (!error) {
          setIsAdmin(data || false);
        } else {
          setIsAdmin(false);
        }
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();

    // Fetch avatar URL
    const fetchAvatar = async () => {
      if (!user) { setAvatarUrl(null); return; }
      try {
        const { data } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('user_id', user.id)
          .maybeSingle();
        setAvatarUrl(data?.avatar_url || null);
      } catch {
        setAvatarUrl(null);
      }
    };
    fetchAvatar();

    // Check workshop leader status
    const checkWorkshopLeader = () => {
      if (user) {
        supabase
          .from('workshop_leaders')
          .select('is_active, paid_through')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data }) => {
            setIsWorkshopLeader(
              !!(data?.is_active && data?.paid_through && new Date(data.paid_through) > new Date())
            );
          });
      } else {
        setIsWorkshopLeader(false);
      }
    };
    checkWorkshopLeader();

    // Re-check when workshop setup completes
    const handleActivated = () => checkWorkshopLeader();
    window.addEventListener('workshop-leader-activated', handleActivated);
    return () => window.removeEventListener('workshop-leader-activated', handleActivated);
  }, [user]);

  if (isMobile) {
    return (
      <>
        {/* Top bar: logo + user menu */}
        <div className="flex items-center justify-between px-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="relative">
              <img
                src="/lovable-uploads/48a0f3cd-b38c-4efd-956b-0555da8f4f45.png"
                alt="Milky Way Planner"
                className="h-10 cursor-pointer"
              />
            </div>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="p-2 hover:bg-muted" aria-label="Open navigation menu">
                <Menu className="w-5 h-5 text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background/95 backdrop-blur-sm border shadow-xl">
              {user && (
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium truncate text-foreground">{user.email}</p>
                </div>
              )}
              <DropdownMenuItem asChild>
                <Link to={plannerTo} className={`flex items-center w-full ${isActive(plannerTo) ? 'text-gold font-medium' : ''}`}>
                  <Star className="w-4 h-4 mr-2" />
                  {plannerLabel}
                </Link>
              </DropdownMenuItem>
              {getNavLinks(!!user).map((link) => {
                if (link.to === '/resources') {
                  return (
                    <React.Fragment key={link.to}>
                      <DropdownMenuItem asChild>
                        <Link to="/resources" className={`flex items-center w-full ${isActive('/resources') ? 'text-gold font-medium' : ''}`}>
                          <link.icon className="w-4 h-4 mr-2" />
                          Resources
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/intro-to-astrophotography" className="flex items-center w-full pl-6 text-sm">
                          Intro to Astrophotography
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dark-sky-locations" className="flex items-center w-full pl-6 text-sm">
                          Dark Sky Locations
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/milky-way-calendar/${currentCalendarYear()}`} className="flex items-center w-full pl-6 text-sm">
                          Milky Way Calendar
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/about" className="flex items-center w-full pl-6 text-sm">
                          About
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Location Guides</div>
                      {[
                        { to: '/best-milky-way-locations-colorado', label: 'Colorado' },
                        { to: '/best-milky-way-locations-utah', label: 'Utah' },
                        { to: '/best-milky-way-locations-new-mexico', label: 'New Mexico' },
                        { to: '/best-milky-way-locations-arizona', label: 'Arizona' },
                      ].map((guide) => (
                        <DropdownMenuItem key={guide.to} asChild>
                          <Link to={guide.to} className="flex items-center w-full pl-6 text-sm">
                            {guide.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </React.Fragment>
                  );
                }
                return (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link to={link.to} className={`flex items-center w-full ${isActive(link.to) ? 'text-gold font-medium' : ''}`}>
                      <link.icon className="w-4 h-4 mr-2" />
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
              {user && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center w-full">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Planning Hub
                    </Link>
                  </DropdownMenuItem>
                  {isWorkshopLeader && (
                    <DropdownMenuItem asChild>
                      <Link to="/workshops/manage" className="flex items-center w-full">
                        <Camera className="w-4 h-4 mr-2" />
                        Workshop Manager
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=subscription" className="flex items-center w-full">
                      <Settings className="w-4 h-4 mr-2" />
                      Subscription
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/contact" className="flex items-center w-full">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Support
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center w-full">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut} className="flex items-center w-full text-red-600 hover:bg-red-50">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </>
              )}
              {!user && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onShowAuthModal} className="flex items-center w-full">
                    <User className="w-4 h-4 mr-2" />
                    Sign In
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Bottom tab bar */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border shadow-[0_-1px_3px_rgba(0,0,0,0.08)]">
          <div className="flex items-center justify-around py-1.5">
            {[{ to: plannerTo, label: plannerLabel, icon: Star }, ...getNavLinks(!!user)].map((link) => {
              const Icon = link.icon;
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
                    active ? 'text-gold' : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{link.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </>
    );
  }

  return (
    <div className="flex items-center justify-between">
      {/* Logo */}
      <Link to="/" className="flex items-center group">
        <div className="relative">
          <img
            src="/lovable-uploads/48a0f3cd-b38c-4efd-956b-0555da8f4f45.png"
            alt="Milky Way Planner"
            className="h-14 cursor-pointer transition-transform group-hover:scale-105"
          />
        </div>
      </Link>

      {/* Nav + auth — right-aligned */}
      <div className="flex items-center gap-1">
      <nav className="flex items-center gap-1">
        {[{ to: plannerTo, label: plannerLabel, icon: Star }, ...getNavLinks(!!user)].map((link) => {
          const Icon = link.icon;
          if (link.to === '/resources') {
            return (
              <DropdownMenu key={link.to}>
                <DropdownMenuTrigger asChild>
                  <button className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/resources') || location.pathname.startsWith('/best-milky-way') || location.pathname.startsWith('/intro-to-astrophotography') || location.pathname.startsWith('/learn') || location.pathname === '/dark-sky-locations'
                      ? 'text-gold'
                      : 'text-foreground/70 hover:text-gold'
                  }`}>
                    <Icon className="w-4 h-4" />
                    Resources
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-background/95 backdrop-blur-sm border shadow-xl">
                  <DropdownMenuItem asChild>
                    <Link to="/resources" className="flex items-center w-full">Resources</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/intro-to-astrophotography" className="flex items-center w-full">Intro to Astrophotography</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dark-sky-locations" className="flex items-center w-full">Dark Sky Locations</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={`/milky-way-calendar/${currentCalendarYear()}`} className="flex items-center w-full">Milky Way Calendar</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/about" className="flex items-center w-full">About</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Milky Way Location Guides</div>
                  {[
                    { to: '/best-milky-way-locations-colorado', label: 'Colorado' },
                    { to: '/best-milky-way-locations-utah', label: 'Utah' },
                    { to: '/best-milky-way-locations-new-mexico', label: 'New Mexico' },
                    { to: '/best-milky-way-locations-arizona', label: 'Arizona' },
                  ].map((guide) => (
                    <DropdownMenuItem key={guide.to} asChild>
                      <Link to={guide.to} className="flex items-center w-full pl-3 text-sm">{guide.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          return (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? 'text-gold'
                  : 'text-foreground/70 hover:text-gold'
              }`}
            >
              <Icon className="w-4 h-4" />
              {link.label}
            </Link>
          );
        })}
      </nav>

      <div className="flex items-center gap-2">
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors border border-border">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <span className="text-sm font-medium text-foreground truncate max-w-32">
                  {user.email}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background/95 backdrop-blur-sm border border-border shadow-xl rounded-xl">
              <div className="px-3 py-3 border-b border-border">
                <p className="text-sm font-medium text-foreground">{user.email}</p>
                <p className="text-xs text-muted-foreground mt-1">Manage your account</p>
              </div>
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="flex items-center w-full hover:bg-muted text-foreground">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Planning Hub
                </Link>
              </DropdownMenuItem>
              {isWorkshopLeader && (
                <DropdownMenuItem asChild>
                  <Link to="/workshops/manage" className="flex items-center w-full hover:bg-muted text-foreground">
                    <Camera className="w-4 h-4 mr-2" />
                    Workshop Manager
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard?tab=profile" className="flex items-center w-full hover:bg-muted text-foreground">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard?tab=subscription" className="flex items-center w-full hover:bg-muted text-foreground">
                  <Settings className="w-4 h-4 mr-2" />
                  Subscription
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/contact" className="flex items-center w-full hover:bg-muted text-foreground">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Support
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center w-full hover:bg-muted text-foreground">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onSignOut} className="flex items-center w-full text-red-600 hover:bg-red-50 focus:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            onClick={onShowAuthModal}
            variant="outline"
            size="sm"
            className="border-border text-foreground/70 hover:text-gold hover:border-gold/30 transition-colors"
          >
            Sign In
          </Button>
        )}
      </div>
      </div>
    </div>
  );
};
